'use client';

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@repo/ux/carousel';
import { EnrichedHeroConfig } from '@/lib/getHostConfig';
import { cn } from '@/lib/utils';

const AUTOPLAY_INTERVAL_MS = 5000;

interface HeroCarouselProps {
  config: EnrichedHeroConfig | null;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ config }) => {
  const slides = config?.images ?? [];
  const url = config?.imageFolder ?? '';
  const pauseOnHover = config?.pauseOnHover ?? false;

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Track selected slide index for dot pagination
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    onSelect();
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // Autoplay — loops forward, pauses on hover
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  useEffect(() => {
    if (!api || isPaused || slides.length <= 1) return;
    const timer = setInterval(scrollNext, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [api, isPaused, scrollNext, slides.length]);

  if (slides.length === 0) return null;

  const multiSlide = slides.length > 1;

  return (
    <div
      className="relative"
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
    >
      <Carousel
        opts={{ loop: true }}
        setApi={setApi}
        className="h-[30rem] md:h-[calc(70vh)]"
      >
        <CarouselContent className="-ml-0 h-[30rem] md:h-[calc(70vh)]">
          {slides.map((slide, index) => (
            <CarouselItem key={index} className="pl-0">
              <div
                className="flex h-[30rem] flex-col bg-cover bg-top bg-no-repeat md:h-[calc(70vh)]"
                style={{
                  backgroundImage: `url('${url ? `${url}/${slide}` : slide}')`,
                }}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Overlay: title/subtitle */}
        <div className="w-full absolute inset-0 pointer-events-none flex items-end [background:linear-gradient(to_bottom,transparent_50%,rgba(0,0,0,0.8)_100%)]">
          <div className="pb-20 sm:pl-28 sm:pb-40 text-white mx-auto max-w-xs sm:max-w-full sm:mx-0 ">
            <div className="text-3xl sm:text-4.5xl">{config?.mainTitle}</div>
            <div className="sm:text-2xl">{config?.subTitle}</div>
          </div>
        </div>

        {multiSlide && (
          <>
            {/* Previous arrow */}
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              className="absolute inset-y-0 start-0 inline-flex h-full w-12 items-center justify-center text-gray-400 hover:bg-white/20 hover:text-gray-800 focus:bg-white/20 focus:outline-hidden cursor-pointer"
              aria-label="Previous slide"
            >
              <ChevronLeftIcon className="size-8" />
            </button>

            {/* Next arrow */}
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              className="absolute inset-y-0 end-0 inline-flex h-full w-12 items-center justify-center text-gray-400 hover:bg-white/20 hover:text-gray-800 focus:bg-white/20 focus:outline-hidden cursor-pointer"
              aria-label="Next slide"
            >
              <ChevronRightIcon className="size-8" />
            </button>

            {/* Dot pagination */}
            <div className="absolute bottom-3 end-0 start-0 flex justify-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => api?.scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={cn(
                    'size-3 rounded-full border cursor-pointer transition-colors',
                    index === current
                      ? 'bg-blue-700 border-blue-700 dark:bg-blue-500 dark:border-blue-500'
                      : 'border-gray-400 dark:border-neutral-600'
                  )}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
    </div>
  );
};

export default HeroCarousel;
